/**
 * Resumo SIMULADO por funil, por canal, por oferta e o retrato "Agora" (RNF19)
 * -- nao e producao.
 *
 * Decisoes nao obvias:
 *  1. O volume e proporcional aos DIAS da janela e varia por EMPRESA (hash do
 *     id + feeBps e ofertas do catalogo). Trocar o periodo ou a empresa no
 *     cabecalho muda os numeros, como vai acontecer com o backend real.
 *     Janela "desde o inicio" (nula) vale 30 dias e NAO tem periodo anterior.
 *  2. Nada de Math.random e nada de float em dinheiro: as vendas sao
 *     distribuidas entre as ofertas do catalogo (precos em centavos), a
 *     comissao e `round(bruto * feeBps / 10000)` e o canal "vendedora" recebe
 *     o RESTO da divisao, para as fatias do anel somarem exatamente o total.
 *  3. As OFERTAS sao a fonte e o funil e a soma: vendas, abertos e entradas
 *     sao repartidos entre as ofertas por maior resto (`repartir`), entao a
 *     tabela "Por oferta" nunca discorda da linha do funil nem da faixa TOTAL.
 *     Os pesos de entrada sao desiguais de proposito: e o que faz existir uma
 *     oferta que concentra abandono e recupera pouco -- o insight mais util da
 *     tela precisa de dado que o mostre.
 *  4. O PERIODO ANTERIOR e derivado do atual por um segundo hash: entradas
 *     entre 88% e 112% das atuais e taxa 3 pontos para cima ou para baixo.
 *     Assim a comparacao tem altas e quedas de verdade, sem sair da ordem de
 *     grandeza -- um "anterior" sorteado do zero produziria variacoes de 60%
 *     que nenhum lojista veria na vida.
 *  5. O MES CORRENTE e independente do filtro: e sempre setembro/2026 na
 *     previa (`AGORA_SIMULADO`), com 24 dias decorridos de 30.
 *  6. Estes totais NAO batem com `simularMetricas` de lib/api.ts, que e um
 *     conjunto fixo com outra escala. Limitacao conhecida da previa,
 *     registrada no relatorio do modulo.
 */

import { AGORA_SIMULADO, menosMinutos } from '../../lib/api';
import { empresaPorId, equipeDaEmpresa, ofertasDaEmpresa } from '../../lib/mock/catalogo';
import { rangeDays } from '../../lib/period';
import type { FunnelKind, PeriodRange, ProductOffer, TeamMember } from '../../types';
import type {
  BestHour,
  ChannelSummary,
  FunnelSummary,
  FunnelsSummary,
  LiveSnapshot,
  MonthProgress,
  OfferSummary,
  PeriodBounds,
  ReturnMetrics,
} from './types';

const DIAS_PADRAO = 30;
const DIAS_MAXIMO = 365;
const FEE_PADRAO_BPS = 1_500;
const FUNIS_PADRAO: readonly FunnelKind[] = ['carrinho_abandonado'];
const DIA_MS = 86_400_000;
/** Limiar de "alto valor" para o retrato Agora: R$ 500,00. */
const ALTO_VALOR_CENTS = 50_000;

/** Hash pequeno e estavel do id: mesmo id, mesmo numero, em qualquer maquina. */
export function hashSimulado(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) % 10_007;
  }
  return h;
}

/** Dias cobertos pela janela, com teto; janela nula (desde o inicio) vale 30. */
export function diasDaJanela(range: PeriodRange): number {
  const dias = rangeDays(range);
  if (dias === null) {
    return DIAS_PADRAO;
  }
  return Math.max(1, Math.min(DIAS_MAXIMO, dias));
}

/** `base * bps / 10000`, arredondado para inteiro. Substitui qualquer `* 0.21`. */
function proporcao(base: number, bps: number): number {
  return Math.round((base * bps) / 10_000);
}

/**
 * Reparte `total` em partes inteiras proporcionais aos pesos, somando
 * EXATAMENTE `total` (metodo do maior resto). Nenhum float: o quociente e
 * inteiro e o resto decide quem leva a unidade que sobrou.
 */
function repartir(total: number, pesos: readonly number[]): readonly number[] {
  const soma = pesos.reduce((acc, p) => acc + p, 0);
  if (total <= 0 || pesos.length === 0 || soma <= 0) {
    return pesos.map(() => 0);
  }
  const partes = pesos.map((p) => Math.floor((total * p) / soma));
  const restos = pesos.map((p, i) => ({ i, resto: (total * p) % soma })).sort((a, b) => b.resto - a.resto || a.i - b.i);
  let falta = total - partes.reduce((acc, v) => acc + v, 0);
  for (const { i } of restos) {
    if (falta <= 0) {
      break;
    }
    partes[i] = (partes[i] ?? 0) + 1;
    falta -= 1;
  }
  return partes;
}

/** Round-robin: as primeiras `resto` posicoes levam uma unidade a mais. */
function repartirIgual(total: number, quantidade: number): readonly number[] {
  if (quantidade <= 0) {
    return [];
  }
  const base = Math.floor(total / quantidade);
  const resto = total % quantidade;
  return Array.from({ length: quantidade }, (_, i) => base + (i < resto ? 1 : 0));
}

function fecharMetricas(recoveredAmountCents: number, commissionCents: number, recoveredCount: number, enteredCount: number): ReturnMetrics {
  return {
    recoveredAmountCents,
    commissionCents,
    netAmountCents: recoveredAmountCents - commissionCents,
    recoveredCount,
    averageTicketCents: recoveredCount === 0 ? 0 : Math.round(recoveredAmountCents / recoveredCount),
    recoveryRateBps: enteredCount === 0 ? 0 : Math.round((recoveredCount * 10_000) / enteredCount),
  };
}

interface ContextoEmpresa {
  readonly companyId: string;
  readonly feeBps: number;
  readonly hash: number;
  /** Segundo hash, para o periodo anterior e o retrato Agora nao copiarem o atual. */
  readonly hashAnterior: number;
  readonly dias: number;
  readonly ofertas: readonly ProductOffer[];
  readonly equipe: readonly TeamMember[];
  readonly onboarding: boolean;
}

interface Volumes {
  readonly enteredCount: number;
  readonly recoveredCount: number;
  readonly openCount: number;
}

/** Volumes de um funil na janela atual ou na anterior de mesmo tamanho. */
function volumesDoFunil(kind: FunnelKind, ctx: ContextoEmpresa, fase: 'atual' | 'anterior'): Volumes {
  const carrinho = kind === 'carrinho_abandonado';
  // Carrinho: 9 a 16 abandonos por dia; expirados: 2 a 4 assinaturas vencidas por dia.
  const porDia = carrinho ? 9 + (ctx.hash % 8) : 2 + (ctx.hash % 3);
  let enteredCount = Math.max(1, ctx.dias * porDia);
  // Taxa de recuperacao: 17% a 23% no carrinho, 28% a 33% nos expirados.
  let taxaBps = carrinho ? 1_700 + (ctx.hash % 7) * 100 : 2_800 + (ctx.hash % 6) * 100;
  if (fase === 'anterior') {
    const h = (ctx.hashAnterior + (carrinho ? 0 : 41)) % 10_007;
    enteredCount = Math.max(1, proporcao(enteredCount, 8_800 + (h % 25) * 100));
    taxaBps = Math.max(300, taxaBps + ((h % 7) - 3) * 100);
  }
  const recoveredCount = proporcao(enteredCount, taxaBps);
  // Ainda em aberto: 12% a 16% do carrinho (dentro dos 7 dias); 40% a 44% dos expirados.
  const abertoBps = carrinho ? 1_200 + (ctx.hash % 5) * 100 : 4_000 + (ctx.hash % 5) * 100;
  const openCount = Math.max(0, Math.min(enteredCount - recoveredCount, proporcao(enteredCount, abertoBps)));
  return { enteredCount, recoveredCount, openCount };
}

/** Expirados so vendem assinatura (recorrente); carrinho, compra unica. */
function ofertasElegiveis(kind: FunnelKind, ofertas: readonly ProductOffer[]): readonly ProductOffer[] {
  const ativas = ofertas.filter((o) => o.active);
  const elegiveis = ativas.filter((o) => o.recurring === (kind === 'expirados'));
  return elegiveis.length > 0 ? elegiveis : ativas;
}

function ofertaPadrao(kind: FunnelKind, companyId: string): ProductOffer {
  return {
    id: `SIMULADO-oferta-${kind}`,
    companyId,
    name: kind === 'expirados' ? 'Plano de exemplo' : 'Produto de exemplo',
    priceCents: kind === 'expirados' ? 9_700 : 24_900,
    recurring: kind === 'expirados',
    provider: 'kiwify',
    active: true,
  };
}

interface FunilMontado {
  readonly resumo: FunnelSummary;
  readonly ofertas: readonly OfferSummary[];
}

function montarFunil(kind: FunnelKind, ctx: ContextoEmpresa, posicao: number): FunilMontado {
  const atual = volumesDoFunil(kind, ctx, 'atual');
  const anterior = volumesDoFunil(kind, ctx, 'anterior');

  const lista = ofertasElegiveis(kind, ctx.ofertas);
  const ofertas = lista.length > 0 ? lista : [ofertaPadrao(kind, ctx.companyId)];
  const n = ofertas.length;

  // Vendas em round-robin; abertos e o restante das entradas com pesos desiguais.
  const vendas = repartirIgual(atual.recoveredCount, n);
  const abertos = repartir(
    atual.openCount,
    ofertas.map((_, i) => 1 + ((ctx.hash + i * 13) % 5)),
  );
  const restante = repartir(
    atual.enteredCount - atual.recoveredCount - atual.openCount,
    ofertas.map((_, i) => 1 + ((ctx.hash + i * 7) % 6)),
  );

  const porOferta: readonly OfferSummary[] = ofertas.map((oferta, i) => {
    const recoveredCount = vendas[i] ?? 0;
    const openCount = abertos[i] ?? 0;
    return {
      offerId: oferta.id,
      name: oferta.name,
      kind,
      provider: oferta.provider,
      priceCents: oferta.priceCents,
      enteredCount: recoveredCount + openCount + (restante[i] ?? 0),
      recoveredCount,
      recoveredAmountCents: recoveredCount * oferta.priceCents,
      openCount,
      openAmountCents: openCount * oferta.priceCents,
    };
  });

  const recoveredAmountCents = porOferta.reduce((acc, o) => acc + o.recoveredAmountCents, 0);
  const openAmountCents = porOferta.reduce((acc, o) => acc + o.openAmountCents, 0);
  const commissionCents = proporcao(recoveredAmountCents, ctx.feeBps);

  // Periodo anterior: mesma distribuicao de precos, volumes da fase anterior.
  const vendasAnteriores = repartirIgual(anterior.recoveredCount, n);
  const brutoAnterior = ofertas.reduce((acc, o, i) => acc + o.priceCents * (vendasAnteriores[i] ?? 0), 0);
  const comissaoAnterior = proporcao(brutoAnterior, ctx.feeBps);

  const responsavel = ctx.equipe[(ctx.hash + posicao) % Math.max(1, ctx.equipe.length)];
  // Contato chegou a 90% a 97% de quem entrou (o resto: numero invalido ou ainda na janela).
  const contactedCount = Math.min(atual.enteredCount, proporcao(atual.enteredCount, 9_000 + (ctx.hash % 8) * 100));

  const resumo: FunnelSummary = {
    kind,
    active: !ctx.onboarding || kind === 'carrinho_abandonado',
    enteredCount: atual.enteredCount,
    openCount: atual.openCount,
    openAmountCents,
    contactedCount,
    // Segundo funil da empresa nasce sem responsavel: exercita o "proximo passo" de designar alguem.
    assigneeId: responsavel === undefined || posicao === 1 ? null : responsavel.id,
    previous: {
      ...fecharMetricas(brutoAnterior, comissaoAnterior, anterior.recoveredCount, anterior.enteredCount),
      enteredCount: anterior.enteredCount,
      openCount: anterior.openCount,
    },
    ...fecharMetricas(recoveredAmountCents, commissionCents, atual.recoveredCount, atual.enteredCount),
  };
  return { resumo, ofertas: porOferta };
}

/**
 * Reparte o total entre os quatro canais. Voz leva 45-52%, WhatsApp 24-29%,
 * e-mail 7-10% e a vendedora fica com o resto, o que garante soma exata.
 */
function dividirPorCanal(total: ReturnMetrics, hash: number): readonly ChannelSummary[] {
  const vozBps = 4_500 + (hash % 8) * 100;
  const whatsappBps = 2_400 + (hash % 6) * 100;
  const emailBps = 700 + (hash % 4) * 100;

  const voz: ChannelSummary = {
    channel: 'voice',
    recoveredAmountCents: proporcao(total.recoveredAmountCents, vozBps),
    recoveredCount: proporcao(total.recoveredCount, vozBps),
  };
  const whatsapp: ChannelSummary = {
    channel: 'whatsapp',
    recoveredAmountCents: proporcao(total.recoveredAmountCents, whatsappBps),
    recoveredCount: proporcao(total.recoveredCount, whatsappBps),
  };
  const email: ChannelSummary = {
    channel: 'email',
    recoveredAmountCents: proporcao(total.recoveredAmountCents, emailBps),
    recoveredCount: proporcao(total.recoveredCount, emailBps),
  };
  const vendedora: ChannelSummary = {
    channel: 'seller',
    recoveredAmountCents: Math.max(0, total.recoveredAmountCents - voz.recoveredAmountCents - whatsapp.recoveredAmountCents - email.recoveredAmountCents),
    recoveredCount: Math.max(0, total.recoveredCount - voz.recoveredCount - whatsapp.recoveredCount - email.recoveredCount),
  };
  return [voz, whatsapp, email, vendedora];
}

function somarFunis(funis: readonly FunnelSummary[]): ReturnMetrics {
  const soma = funis.reduce(
    (acc, f) => ({
      bruto: acc.bruto + f.recoveredAmountCents,
      comissao: acc.comissao + f.commissionCents,
      vendas: acc.vendas + f.recoveredCount,
      entraram: acc.entraram + f.enteredCount,
    }),
    { bruto: 0, comissao: 0, vendas: 0, entraram: 0 },
  );
  return fecharMetricas(soma.bruto, soma.comissao, soma.vendas, soma.entraram);
}

function somarAnteriores(funis: readonly FunnelSummary[]): ReturnMetrics {
  const soma = funis.reduce(
    (acc, f) => ({
      bruto: acc.bruto + (f.previous?.recoveredAmountCents ?? 0),
      comissao: acc.comissao + (f.previous?.commissionCents ?? 0),
      vendas: acc.vendas + (f.previous?.recoveredCount ?? 0),
      entraram: acc.entraram + (f.previous?.enteredCount ?? 0),
    }),
    { bruto: 0, comissao: 0, vendas: 0, entraram: 0 },
  );
  return fecharMetricas(soma.bruto, soma.comissao, soma.vendas, soma.entraram);
}

/** Janela de mesmo tamanho imediatamente antes de `range`; `null` em "desde o inicio". */
function periodoAnterior(range: PeriodRange): PeriodBounds | null {
  if (range.from === null || range.to === null) {
    return null;
  }
  const from = new Date(range.from).getTime();
  const to = new Date(range.to).getTime();
  const largura = Math.max(DIA_MS, to - from);
  return { start: new Date(from - largura).toISOString(), end: new Date(from).toISOString() };
}

function melhorHorario(funis: readonly FunnelSummary[], hash: number): BestHour | null {
  const ligacoes = funis.reduce((acc, f) => acc + f.contactedCount, 0);
  if (ligacoes < 10) {
    return null;
  }
  // Entre 10h e 18h; concentra 9% a 13% das ligacoes; atende 52% a 60% delas.
  const calls = Math.max(1, proporcao(ligacoes, 900 + (hash % 5) * 100));
  return {
    hour: 10 + (hash % 9),
    calls,
    answered: Math.min(calls, proporcao(calls, 5_200 + (hash % 9) * 100)),
  };
}

function mesCorrente(ctx: ContextoEmpresa, kinds: readonly FunnelKind[]): MonthProgress {
  const agora = AGORA_SIMULADO;
  const ano = agora.getUTCFullYear();
  const mes = agora.getUTCMonth();
  const daysInMonth = new Date(Date.UTC(ano, mes + 1, 0)).getUTCDate();
  const daysElapsed = agora.getUTCDate();
  const monthKey = `${ano}-${String(mes + 1).padStart(2, '0')}`;

  // Ritmo do mes em cima de uma janela de 30 dias, independente do filtro.
  const ctx30: ContextoEmpresa = { ...ctx, dias: DIAS_PADRAO };
  const total30 = somarFunis(kinds.map((kind, i) => montarFunil(kind, ctx30, i).resumo));
  const ritmoBps = 9_400 + (ctx.hash % 13) * 100;
  const realizedAmountCents = proporcao(Math.round((total30.recoveredAmountCents * daysElapsed) / DIAS_PADRAO), ritmoBps);
  const realizedCount = proporcao(Math.round((total30.recoveredCount * daysElapsed) / DIAS_PADRAO), ritmoBps);
  const anteriorBps = 9_000 + (ctx.hashAnterior % 17) * 100;
  const previousMonthAmountCents = proporcao(total30.recoveredAmountCents, anteriorBps);
  const previousMonthCount = proporcao(total30.recoveredCount, anteriorBps);

  return {
    monthKey,
    daysElapsed,
    daysInMonth,
    realizedAmountCents,
    realizedNetCents: realizedAmountCents - proporcao(realizedAmountCents, ctx.feeBps),
    realizedCount,
    previousMonthAmountCents,
    previousMonthNetCents: previousMonthAmountCents - proporcao(previousMonthAmountCents, ctx.feeBps),
    previousMonthCount,
  };
}

function contexto(companyId: string, range: PeriodRange): ContextoEmpresa {
  const empresa = empresaPorId(companyId);
  return {
    companyId,
    feeBps: empresa === null ? FEE_PADRAO_BPS : empresa.feeBps,
    hash: hashSimulado(companyId),
    hashAnterior: hashSimulado(`${companyId}:anterior`),
    dias: diasDaJanela(range),
    ofertas: ofertasDaEmpresa(companyId),
    equipe: equipeDaEmpresa(companyId),
    onboarding: empresa?.status === 'onboarding',
  };
}

export function simularResumoFunis(companyId: string, range: PeriodRange): FunnelsSummary {
  const empresa = empresaPorId(companyId);
  const ctx = contexto(companyId, range);
  const kinds = empresa === null ? FUNIS_PADRAO : empresa.funnels;
  const montados = kinds.map((kind, i) => montarFunil(kind, ctx, i));
  const funis = montados.map((m) => m.resumo);
  const total = somarFunis(funis);
  const anterior = periodoAnterior(range);

  return {
    companyId,
    feeBps: ctx.feeBps,
    total,
    previousTotal: anterior === null ? null : somarAnteriores(funis),
    previousPeriod: anterior,
    funnels: anterior === null ? funis.map((f) => ({ ...f, previous: null })) : funis,
    channels: dividirPorCanal(total, ctx.hash),
    offers: montados.flatMap((m) => m.ofertas).filter((o) => o.enteredCount > 0),
    bestHour: melhorHorario(funis, ctx.hash),
    month: mesCorrente(ctx, kinds),
    periodStart: range.from ?? menosMinutos(60 * 24 * DIAS_PADRAO),
    periodEnd: range.to ?? AGORA_SIMULADO.toISOString(),
  };
}

/** Soma os precos de `quantidade` itens percorrendo as ofertas em ciclo. */
function valorDeFila(ofertas: readonly ProductOffer[], quantidade: number, precoPadraoCents: number, deslocamento: number): number {
  if (quantidade <= 0) {
    return 0;
  }
  if (ofertas.length === 0) {
    return quantidade * precoPadraoCents;
  }
  let soma = 0;
  for (let i = 0; i < quantidade; i += 1) {
    soma += ofertas[(i + deslocamento) % ofertas.length]?.priceCents ?? precoPadraoCents;
  }
  return soma;
}

/**
 * Retrato do motor neste instante. Empresa em implantacao ainda nao tem
 * evento nenhum: tudo zero e sem "ultimo evento", para a tela exercitar o
 * estado vazio em vez de inventar movimento.
 */
export function simularAgora(companyId: string): LiveSnapshot {
  const empresa = empresaPorId(companyId);
  const ativas = ofertasDaEmpresa(companyId).filter((o) => o.active);
  const h = hashSimulado(`${companyId}:agora`);
  const capturedAt = AGORA_SIMULADO.toISOString();

  if (empresa?.status === 'onboarding') {
    return {
      companyId,
      capturedAt,
      coolingCount: 0,
      coolingAmountCents: 0,
      queuedCount: 0,
      queuedAmountCents: 0,
      callsInProgress: 0,
      awaitingReplyCount: 0,
      withSellerCount: 0,
      nextTouchAt: null,
      lastEventAt: null,
      todayEnteredCount: 0,
      todayRecoveredCount: 0,
      todayRecoveredAmountCents: 0,
      highValueOpen: { count: 0, amountCents: 0, thresholdCents: ALTO_VALOR_CENTS },
    };
  }

  const coolingCount = 1 + (h % 4);
  const queuedCount = 6 + (h % 12);
  const todayEnteredCount = 5 + (h % 9);
  const todayRecoveredCount = 1 + (h % 3);
  const caras = ativas.filter((o) => o.priceCents >= ALTO_VALOR_CENTS);
  const altoValorCount = caras.length === 0 ? 0 : 2 + (h % 5);

  return {
    companyId,
    capturedAt,
    coolingCount,
    coolingAmountCents: valorDeFila(ativas, coolingCount, 24_900, h),
    queuedCount,
    queuedAmountCents: valorDeFila(ativas, queuedCount, 24_900, h + 2),
    callsInProgress: h % 3,
    awaitingReplyCount: 4 + (h % 9),
    withSellerCount: 1 + (h % 5),
    nextTouchAt: menosMinutos(-(2 + (h % 11))),
    lastEventAt: menosMinutos(1 + (h % 6)),
    todayEnteredCount,
    todayRecoveredCount,
    todayRecoveredAmountCents: valorDeFila(ativas, todayRecoveredCount, 24_900, h + 5),
    highValueOpen: {
      count: altoValorCount,
      amountCents: valorDeFila(caras, altoValorCount, ALTO_VALOR_CENTS, h),
      thresholdCents: ALTO_VALOR_CENTS,
    },
  };
}
